import { z } from "zod";
import { optionalUuid, optionalDateString } from "./_helpers/optional";
import { ExternalIdEntrySchema } from "./external-id.schema";

// Service/maintenance AGREEMENT instance (ADR-0048 / ADV-1175). A service_plan
// stamped onto a specific customer/property. end_date / renewal_date /
// next_visit_date / next_bill_date are DB-derived (finance.compute_service_agreement_dates)
// — they appear on the read schema only, never on insert/update.

export const serviceAgreementStatuses = [
  "pending",
  "active",
  "paused",
  "expired",
  "cancelled",
] as const;

export const ServiceAgreementSchema = z.object({
  id: z.string().uuid().describe("ID"),
  account_id: z.string().uuid().describe("Account"),
  business_id: z.string().uuid().describe("Business"),
  service_plan_id: z.string().uuid().describe("Service Plan"),
  customer_contact_role_id: z.string().uuid().describe("Customer"),
  property_id: z.string().uuid().nullable().describe("Property"),
  location_id: z.string().uuid().nullable().describe("Location"),
  opportunity_id: z.string().uuid().nullable().describe("Opportunity"),
  start_date: z.string().nullable().describe("Start Date"),
  // DB-derived (read-only — see finance.compute_service_agreement_dates).
  end_date: z.string().nullable().describe("End Date"),
  renewal_date: z.string().nullable().describe("Renewal Date"),
  next_visit_date: z.string().nullable().describe("Next Visit Date"),
  next_bill_date: z.string().nullable().describe("Next Bill Date"),
  status: z.enum(serviceAgreementStatuses).describe("Status"),
  billing_amount: z.number().nullable().describe("Billing Amount"),
  billing_anchor_day: z
    .number()
    .int()
    .nullable()
    .describe("Billing Anchor Day"),
  cancelled_reason: z.string().nullable().describe("Cancelled Reason"),
  cancelled_at: z.string().nullable().describe("Cancelled At"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
  external_ids: ExternalIdEntrySchema.array()
    .optional()
    .describe("External IDs"),
});
export type ServiceAgreement = z.infer<typeof ServiceAgreementSchema>;

export const ServiceAgreementInsertSchema = z.object({
  service_plan_id: z.string().uuid().describe("Service Plan"),
  customer_contact_role_id: z.string().uuid().describe("Customer"),
  property_id: optionalUuid().describe("Property"),
  location_id: optionalUuid().describe("Location"),
  opportunity_id: optionalUuid().describe("Opportunity"),
  start_date: optionalDateString().describe("Start Date"),
  status: z
    .enum(serviceAgreementStatuses)
    .default("pending")
    .describe("Status"),
  billing_amount: z
    .number()
    .nonnegative()
    .nullable()
    .optional()
    .describe("Billing Amount"),
  billing_anchor_day: z
    .number()
    .int()
    .min(1)
    .max(31)
    .nullable()
    .optional()
    .describe("Billing Anchor Day"),
  cancelled_reason: z
    .string()
    .nullable()
    .optional()
    .describe("Cancelled Reason"),
  cancelled_at: z.string().nullable().optional().describe("Cancelled At"),
  // end_date / renewal_date / next_visit_date / next_bill_date are DB-derived — omitted.
});

export const ServiceAgreementUpdateSchema =
  ServiceAgreementInsertSchema.partial();

export const ServiceAgreementQuerySchema = z.object({
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  status: z.enum(serviceAgreementStatuses).optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  service_plan_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  customer_contact_role_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  opportunity_id: z.string().uuid().optional(),
  search: z.string().optional().describe("Search"),
});
